import { useState, useEffect } from 'react'
import { Booking } from '@/types'
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { subDays, startOfMonth, endOfMonth } from 'date-fns'
import { TrendingUp, AlertCircle, Clock, FileDown, FileSpreadsheet, Mail, CheckCircle } from 'lucide-react'
import { generateBookingsPDF, generateBookingsExcel, generateOccupancyPDF } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

// Analytics Components
import FinancialSummaryCards from '@/components/analytics/FinancialSummaryCards'
import RevenueChart from '@/components/analytics/RevenueChart'
import BookingStatusChart from '@/components/analytics/BookingStatusChart'
import OccupancyChart from '@/components/analytics/OccupancyChart'
import SpaceRevenueChart from '@/components/analytics/SpaceRevenueChart'
import TransactionsTable from '@/components/analytics/TransactionsTable'

interface ReportsPageProps {
  embedded?: boolean
}

export default function ReportsPage({ embedded = false }: ReportsPageProps) {
  const { currentOrg, userRole } = useAuthStore()
  const [period, setPeriod] = useState<'month' | 'last30' | 'all'>('month')
  const [stats, setStats] = useState<any>(null)
  const [rawBookings, setRawBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [emailSchedule, setEmailSchedule] = useState<{ frequency: string, email: string }>({ frequency: 'never', email: '' })
  const [isScheduleOpen, setIsScheduleOpen] = useState(false)

  const isAdmin = userRole === 'owner' || userRole === 'admin'

  useEffect(() => {
    if (!currentOrg || !isAdmin) return
    loadStats()
  }, [currentOrg?.id, period, isAdmin])

  const loadStats = async () => {
    setLoading(true)
    try {
      const now = new Date()
      let startDate: Date

      if (period === 'month') {
        startDate = startOfMonth(now)
      } else if (period === 'last30') {
        startDate = subDays(now, 30)
      } else {
        startDate = new Date(2024, 0, 1) // Início do projeto
      }

      const q = query(
        collection(db, 'bookings'),
        where('orgId', '==', currentOrg?.id || ''),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endOfMonth(now)))
      )

      const snapshot = await getDocs(q)
      const bookings = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking))
      // Sort by date desc
      bookings.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0))

      setRawBookings(bookings)

      const aggregated = bookings.reduce((acc: any, b: Booking) => {
        acc.totalBookings++
        if (b.status === 'CONFIRMED' || b.status === 'COMPLETED') {
          acc.confirmedBookings++
          acc.totalRevenue += b.totalPrice || 0
        } else if (b.status === 'CANCELED') {
          acc.canceledBookings++
        } else if (b.status === 'PENDING_PAYMENT') {
          acc.pendingPayments++
        }

        // Count statuses for Pie Chart (BookingStatusChart expects specific keys)
        // Adjusting access to match BookingStatusChart props which takes { CONFIRMED: number, ... }
        if (!acc.statusCounts[b.status]) acc.statusCounts[b.status] = 0
        acc.statusCounts[b.status]++

        // Agrupamento por Space name
        const sName = (b as any).spaceName || 'Espaço Desconhecido'
        const sShort = sName.length > 10 ? sName.substring(0, 15) + '...' : sName

        if (!acc.bookingsBySpace[sShort]) {
          acc.bookingsBySpace[sShort] = 0
          acc.revenueBySpace[sShort] = 0
        }

        if (b.status !== 'CANCELED') { // Only count non-canceled for occupancy/revenue potential? Or only confirmed?
          // Usually Occupancy counts confirmed/completed/in_progress
          if (b.status === 'CONFIRMED' || b.status === 'COMPLETED' || b.status === 'IN_PROGRESS') {
            acc.bookingsBySpace[sShort] += 1
            acc.revenueBySpace[sShort] += (b.totalPrice || 0)
          }
        }

        return acc
      }, {
        totalBookings: 0,
        confirmedBookings: 0,
        canceledBookings: 0,
        pendingPayments: 0,
        totalRevenue: 0,
        bookingsBySpace: {},
        revenueBySpace: {},
        statusCounts: {
          CONFIRMED: 0,
          PENDING_APPROVAL: 0,
          PENDING_PAYMENT: 0,
          CANCELED: 0,
          COMPLETED: 0,
          IN_PROGRESS: 0
        }
      })

      // Converter records para arrays formatados para o Recharts
      const chartData = Object.keys(aggregated.bookingsBySpace).map(name => ({
        name: name,
        reservas: aggregated.bookingsBySpace[name],
        receita: aggregated.revenueBySpace[name]
      }))

      // Prepare Revenue Chart Data (aggregated by day/month depending on period)
      // For simplicity, let's group by Day for Month/Last30 and by Month for All
      const revenueDataMap: Record<string, number> = {}
      bookings.forEach(b => {
        if (b.status === 'CONFIRMED' || b.status === 'COMPLETED') {
          const date = b.createdAt?.toDate ? b.createdAt.toDate() : new Date()
          const key = period === 'all'
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            : `${date.getDate()}/${String(date.getMonth() + 1).padStart(2, '0')}`

          revenueDataMap[key] = (revenueDataMap[key] || 0) + (b.totalPrice || 0)
        }
      })

      const revenueChartData = Object.entries(revenueDataMap).map(([date, amount]) => ({
        month: date,
        revenue: amount
      })).reverse() // Since we sorted bookings desc, map iteration order might be desc or random. better sort by date.

      // Sort chart data chronologically
      if (period === 'all') {
        revenueChartData.sort((a, b) => a.month.localeCompare(b.month))
      } else {
        // dd/MM format sorting needs parsing, but let's just reverse if they came from desc bookings
        // Actually, standard js obj keys iteration is insertion order for strings mostly, but better explicit sort.
        // Re-sort map keys
        const sortedKeys = Object.keys(revenueDataMap).sort((a, b) => {
          if (period === 'all') return a.localeCompare(b);
          const [d1, m1] = a.split('/').map(Number);
          const [d2, m2] = b.split('/').map(Number);
          if (m1 !== m2) return m1 - m2;
          return d1 - d2;
        })

        const finalRevenueData = sortedKeys.map(k => ({ month: k, revenue: revenueDataMap[k] }))
        setStats({ ...aggregated, chartData, revenueChartData: finalRevenueData })
      }

      if (period === 'all') {
        // already handled above
        const sortedKeys = Object.keys(revenueDataMap).sort()
        const finalRevenueData = sortedKeys.map(k => ({ month: k, revenue: revenueDataMap[k] }))
        setStats({ ...aggregated, chartData, revenueChartData: finalRevenueData })
      } else {
        // Re-sort map keys for day/month
        const sortedKeys = Object.keys(revenueDataMap).sort((a, b) => {
          const [d1, m1] = a.split('/').map(Number);
          const [d2, m2] = b.split('/').map(Number);
          if (m1 !== m2) return m1 - m2;
          return d1 - d2;
        })
        const finalRevenueData = sortedKeys.map(k => ({ month: k, revenue: revenueDataMap[k] }))
        setStats({ ...aggregated, chartData, revenueChartData: finalRevenueData })
      }

    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSchedule = () => {
    // Aqui seria a integração com o Backend para salvar a cronjob
    toast.success('Agendamento de relatório salvo com sucesso!')
    setIsScheduleOpen(false)
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in duration-700">
        <div className="bg-red-50 p-6 rounded-full mb-6">
          <AlertCircle className="w-12 h-12 text-red-500" />
        </div>
        <h2 className="text-3xl font-black text-[#001c28] uppercase italic tracking-tighter">Acesso Restrito</h2>
        <p className="text-[#001c28]/60 mt-2 max-w-sm">Apenas administradores podem visualizar os relatórios financeiros e de ocupação.</p>
      </div>
    )
  }

  return (
    <div className={`space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700 ${embedded ? '' : 'p-6'}`}>
      {!embedded && (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="space-y-2">
            <h1 className="text-5xl font-black text-[#001c28] uppercase italic tracking-tighter leading-none">Relatórios</h1>
            <p className="text-[#001c28]/60 font-bold uppercase text-[10px] tracking-[0.3em] flex items-center">
              <TrendingUp className="w-3 h-3 mr-2 text-[#d4a94d]" /> Performance da Organização
            </p>
          </div>

          <button
            onClick={() => setIsScheduleOpen(!isScheduleOpen)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl shadow-lg transition-all text-xs font-black uppercase tracking-widest ${isScheduleOpen ? 'bg-[#d4a94d] text-white' : 'bg-white text-[#001c28] border border-[#001c28]/10 hover:border-[#d4a94d]'
              }`}
          >
            <Mail className="w-4 h-4" />
            Agendar Envio
          </button>
        </div>
      )}

      {/* Email Schedule Panel - Collapsible */}
      {isScheduleOpen && (
        <div className="bg-[#001c28] p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-in fade-in slide-in-from-top-4">
          <div className="relative z-10 flex flex-col md:flex-row items-end gap-6">
            <div className="flex-1 space-y-4 w-full">
              <div className="flex items-center gap-2 text-white/50 uppercase tracking-[0.2em] text-[10px] font-black">
                <Clock className="w-4 h-4 text-[#d4a94d]" /> Configurar Envio Automático
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-white text-xs font-bold ml-2">Frequência</label>
                  <select
                    value={emailSchedule.frequency}
                    onChange={(e) => setEmailSchedule({ ...emailSchedule, frequency: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:border-[#d4a94d] outline-none font-bold"
                  >
                    <option value="never" className="bg-[#001c28]">Não enviar</option>
                    <option value="weekly" className="bg-[#001c28]">Semanalmente (Segunda-feira)</option>
                    <option value="monthly" className="bg-[#001c28]">Mensalmente (Dia 1)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-white text-xs font-bold ml-2">Email de Destino</label>
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={emailSchedule.email}
                    onChange={(e) => setEmailSchedule({ ...emailSchedule, email: e.target.value })}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:border-[#d4a94d] outline-none font-bold placeholder:text-white/30"
                  />
                </div>
              </div>
            </div>
            <button
              onClick={handleSaveSchedule}
              className="w-full md:w-auto px-8 py-4 bg-[#d4a94d] text-[#001c28] font-black uppercase tracking-widest rounded-xl hover:bg-white transition-colors shadow-lg shadow-[#d4a94d]/20 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Salvar
            </button>
          </div>
        </div>
      )}

      {/* Control Bar (always visible) */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex bg-white p-2 rounded-3xl border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5">
          {(['month', 'last30', 'all'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${period === p
                ? 'bg-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                : 'text-[#001c28]/40 hover:text-[#001c28]'
                }`}
            >
              {p === 'month' ? 'Este Mês' : p === 'last30' ? 'Últimos 30 Dias' : 'Todo Período'}
            </button>
          ))}
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => generateBookingsPDF(rawBookings, period, currentOrg?.name || 'Relatório')}
            disabled={loading || rawBookings.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#001c28]/5 text-[#001c28] border border-[#001c28]/10 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#001c28] hover:text-[#d4a94d] transition-colors disabled:opacity-50"
          >
            <FileDown className="w-4 h-4" /> PDF Reservas
          </button>
          <button
            onClick={() => generateOccupancyPDF(stats?.chartData || [], period, currentOrg?.name || 'Relatório')}
            disabled={loading || !stats?.chartData?.length}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#001c28]/5 text-[#001c28] border border-[#001c28]/10 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#001c28] hover:text-[#d4a94d] transition-colors disabled:opacity-50"
          >
            <FileDown className="w-4 h-4" /> PDF Ocupação
          </button>
          <button
            onClick={() => generateBookingsExcel(rawBookings, period)}
            disabled={loading || rawBookings.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#001c28]/5 text-[#001c28] border border-[#001c28]/10 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#001c28] hover:text-[#d4a94d] transition-colors disabled:opacity-50"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <FinancialSummaryCards stats={stats} loading={loading} />

      {!loading && stats && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <BookingStatusChart data={stats.statusCounts} />
            <RevenueChart data={stats.revenueChartData || []} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <OccupancyChart data={stats.chartData} />
            <SpaceRevenueChart data={stats.chartData} />
          </div>

          {/* Detailed Transactions Table */}
          <TransactionsTable bookings={rawBookings} loading={loading} />
        </>
      )}
    </div >
  )
}
